import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, Copy, Loader2, Pencil, Sparkles, Undo2, X } from 'lucide-react';
import {
  campaignText,
  describeCampaign,
  type CampaignChange,
  type CampaignSpec,
} from '@vagusx/shared';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useCompileCampaign } from '@/hooks/use-outreach';
import { cn } from '@/lib/utils';

/**
 * The campaign in English, beside the campaign as a schedule — and editable.
 *
 * Two directions, and they are not symmetrical. **Reading** is pure code: the
 * sentences are rendered from the spec on every change, so they cannot drift
 * from what they describe. **Writing** is a compiler, and the only place a model
 * touches a campaign.
 *
 * What makes it safe to apply a compiled edit without a blocking review:
 *
 *  - The compiler is given the current spec as well as the text, and returns
 *    that spec with only what the text says applied. Settings no sentence
 *    mentions cannot be touched.
 *  - Applying re-renders these sentences from the *result*, so a field the model
 *    moved unasked is on screen immediately rather than at publish time.
 *  - The change list says what moved, in the same sentences, including what
 *    nobody asked for.
 *  - Undo is one click, and nothing has reached a patient — publishing is still
 *    a separate, explicit act.
 */

interface TextPanelProps {
  spec: CampaignSpec;
  /** Display names for filter ids, so the text says "Fremont", not a uuid. */
  names: Readonly<Record<string, string>>;
  /**
   * Whether {@link names} is complete.
   *
   * Editing is gated on it. Until every dictionary has arrived the describer
   * degrades an unresolved criterion to a count — "at 2 locations" — which is
   * fine to read but must never be the text the compiler is given: the model
   * cannot resolve that phrase back to two ids, so the criterion is dropped and
   * the audience silently widens to every location.
   */
  namesReady: boolean;
  /** Which sentence's control is currently open, for the active highlight. */
  focusedId: string;
  /** Open the control that owns this clause. */
  onFocus: (sentenceId: string) => void;
  onSpecChange: (spec: CampaignSpec) => void;
}

/** What the last rebuild did, held so it can be read and undone. */
interface Applied {
  changes: CampaignChange[];
  warnings: string[];
  /** The spec before the rebuild, for undo. */
  previous: CampaignSpec;
  /** What the rebuild produced, so an edit made since can retire the undo. */
  produced: CampaignSpec;
}

export function TextPanel({
  spec,
  names,
  namesReady,
  focusedId,
  onFocus,
  onSpecChange,
}: TextPanelProps): React.ReactElement {
  const [copied, setCopied] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [applied, setApplied] = useState<Applied | null>(null);
  const compile = useCompileCampaign();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // What is SHOWN says who is actually checked; the draft the author edits
  // (`campaignText`) stays as stored, so a rebuild cannot rewrite a switch.
  const sections = describeCampaign(spec, { names, asChecked: true });
  const editing = draft !== null;

  /*
    Undo may only ever reach back to the rebuild it belongs to. Once the author
    has changed anything else — dragged a step, opened a row and typed — undoing
    would silently discard that too, which is the opposite of what the button
    says. Retiring the offer is the honest option; the change list stays.
  */
  useEffect(() => {
    setApplied((current) => (current && current.produced !== spec ? null : current));
  }, [spec]);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(campaignText(spec, { names }));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused (permissions, insecure origin). The
      // text is on screen and selectable, so there is nothing to recover —
      // failing quietly beats an error toast for a convenience action.
    }
  };

  const rebuild = async (): Promise<void> => {
    if (draft === null || !draft.trim()) return;

    const previous = spec;
    /*
      Caught here rather than left to `void rebuild()`. `mutateAsync` rejects on
      failure, and an uncaught rejection is a console error — and an overlay
      under dev tooling — for a case the panel already handles: `compile.isError`
      renders the message inline, beside the text that caused it.
    */
    const result = await compile.mutateAsync({ spec: previous, text: draft }).catch(() => null);
    if (!result) return;

    onSpecChange(result.spec);
    setApplied({
      changes: result.changes,
      warnings: result.warnings,
      previous,
      produced: result.spec,
    });
    // Back to the rendered sentences, regenerated from what actually landed —
    // which is the step that makes an unasked-for change visible.
    setDraft(null);
  };

  const undo = (): void => {
    if (!applied) return;
    onSpecChange(applied.previous);
    setApplied(null);
  };

  return (
    <aside className="flex w-80 shrink-0 flex-col border-l bg-muted/20">
      <div className="flex shrink-0 items-center gap-1 border-b px-4 py-3">
        <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          In plain English
        </h2>
        <div className="ml-auto flex items-center gap-0.5">
          {!editing && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label="Copy this description"
                onClick={() => void copy()}
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-primary" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                disabled={!namesReady}
                title={
                  namesReady
                    ? undefined
                    : 'Waiting for location, provider and appointment-type names — editing before they load could drop a filter.'
                }
                onClick={() => {
                  setDraft(campaignText(spec, { names }));
                  // Focus after the textarea exists, not during this render.
                  window.setTimeout(() => textareaRef.current?.focus(), 0);
                }}
              >
                <Pencil className="mr-1.5 h-3 w-3" />
                Edit
              </Button>
            </>
          )}
        </div>
      </div>

      {editing ? (
        <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
          <p className="text-xs text-muted-foreground">
            Change the wording and rebuild. Anything you don't mention stays as it is.
          </p>
          <Textarea
            ref={textareaRef}
            className="min-h-0 flex-1 resize-none font-normal leading-relaxed"
            value={draft}
            maxLength={8000}
            onChange={(e) => setDraft(e.target.value)}
          />
          {compile.isError && (
            <p className="flex items-start gap-1.5 text-xs text-destructive">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              <span>
                {compile.error instanceof Error
                  ? compile.error.message
                  : 'Could not rebuild the campaign from that description.'}
              </span>
            </p>
          )}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              className="flex-1"
              disabled={compile.isPending || !draft.trim()}
              onClick={() => void rebuild()}
            >
              {compile.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              )}
              Rebuild the campaign
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={compile.isPending}
              onClick={() => {
                setDraft(null);
                compile.reset();
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4">
          {sections.map((section) => (
            <section key={section.id} className="space-y-1.5">
              <h3 className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
                {section.heading}
              </h3>
              <p className="text-sm leading-relaxed">
                {section.sentences.map((sentence) => {
                  const moved = applied?.changes.some((c) => c.id === sentence.id);
                  return (
                    <button
                      key={sentence.id}
                      type="button"
                      className={cn(
                        'mr-1 rounded-sm text-left transition-colors hover:bg-primary/10',
                        focusedId === sentence.id
                          ? 'bg-primary/15 text-foreground'
                          : moved
                            ? 'bg-primary/10 text-foreground'
                            : 'text-muted-foreground'
                      )}
                      onClick={() => onFocus(sentence.id)}
                    >
                      {sentence.text}
                    </button>
                  );
                })}
              </p>
            </section>
          ))}
        </div>
      )}

      {/*
        What the rebuild actually did — including what was not asked for. This
        is the whole safety story for applying directly, so it stays on screen
        until the author changes something else rather than fading on a timer.
      */}
      {!editing && applied && (
        <div className="shrink-0 border-t bg-background px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium">
              {applied.changes.length === 0
                ? 'Nothing changed'
                : `${applied.changes.length} ${applied.changes.length === 1 ? 'change' : 'changes'}`}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-auto h-6 px-2 text-xs"
              onClick={undo}
            >
              <Undo2 className="mr-1 h-3 w-3" />
              Undo
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              aria-label="Dismiss the change list"
              onClick={() => setApplied(null)}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>

          <ul className="mt-2 max-h-40 space-y-1.5 overflow-y-auto">
            {applied.changes.map((change) => (
              <li key={change.id} className="text-xs leading-snug">
                <button
                  type="button"
                  className="text-left hover:underline"
                  onClick={() => onFocus(change.id)}
                >
                  {change.kind === 'removed' ? (
                    <span className="text-muted-foreground line-through">{change.before}</span>
                  ) : (
                    <>
                      {change.before && (
                        <span className="text-muted-foreground line-through">{change.before} </span>
                      )}
                      <span>{change.after}</span>
                    </>
                  )}
                </button>
              </li>
            ))}
          </ul>

          {applied.warnings.map((warning) => (
            <p key={warning} className="mt-2 flex items-start gap-1.5 text-xs text-destructive">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              <span>{warning}</span>
            </p>
          ))}
        </div>
      )}

      {!editing && !applied && (
        <p className="shrink-0 border-t px-4 py-3 text-xs text-muted-foreground">
          Always matches the campaign. Click a sentence to open its setting.
        </p>
      )}
    </aside>
  );
}
